import { Box, Typography, useTheme } from "@mui/material";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import PivotablePieChart from "../components/chart/PivotablePieChart";
import { M3Surface } from "../components/m3";
import { computePciData } from "./computePciData";
import { getOrgTokens } from "../theme/style/organizationPalette";

// Chart cards fill their grid cell; the chart draws its own title bar.
const CHART_SURFACE_STYLE = { height: "100%", overflow: "hidden" };

const getPciStatement = (topic = "Product") => {
  const entity = topic.toLowerCase();
  const indicatorName = topic === "Project" ? "Project Circularity Indicator" : topic === "Building" ? "Building Circularity Indicator" : "Product Circularity Indicator";
  return `This graph presents the comprehensive ${indicatorName}, which expands upon raw material data by integrating Design for Disassembly (DfD) principles and Dutch Green Building Council (DGBC) circularity concepts. Rather than tracking mass alone, this metric evaluates the structural architecture of the ${entity}, scoring how easily components can be separated, repaired, or replaced without damaging surrounding parts. It incorporates parameters like connection types (e.g., modular snaps versus irreversible glues), tool accessibility, and the velocity at which sub-assemblies can be harvested for component reuse.`;
};

const CircularPCIChart = ({ setSelectedEnvFactorID, topic = "Product", title, combined_data, productId, showDescription = true }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tok = getOrgTokens(isDark);

  const defaultTitle = `${t(topic === "Project" ? "Project Circularity Indicator" : topic === "Building" ? "Building Circularity Indicator" : "Product Circularity Indicator")}`;
  const effectiveTitle = title ? t(title) : defaultTitle;

  const { chartData, percentageOverallPci, rows } = useMemo(() => {
    return computePciData(combined_data, topic, productId);
  }, [combined_data, topic, productId]);

  const statement = useMemo(() => getPciStatement(topic), [topic]);

  const sideContent = showDescription ? (
    <Typography
      sx={{
        fontFamily: tok.font.family,
        fontSize: "0.78rem",
        lineHeight: 1.55,
        color: tok.text.secondary,
        textAlign: "left",
      }}
    >
      {t(statement)}
    </Typography>
  ) : null;

  return (
    <M3Surface style={CHART_SURFACE_STYLE}>
      <PivotablePieChart
        title={effectiveTitle}
        usages={chartData}
        groupBy={"label"}
        countBy={"value"}
        hollow
        SideContent={sideContent}
        collapsibleSideContent
        sideContentDefaultOpen={false}
        InnerTextComponent={
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flexDirection: "column", height: 1, width: "100%", px: 1, textAlign: "center" }}>
            <Typography
              sx={{
                fontFamily: "Poppins, sans-serif",
                fontSize: "11px",
                fontWeight: 500,
                lineHeight: 1.25,
                color: tok.text.secondary,
                textAlign: "center",
                maxWidth: "140px",
                whiteSpace: "normal",
              }}
            >
              {effectiveTitle}
            </Typography>
            <Typography
              sx={{
                fontFamily: "Poppins, sans-serif",
                fontSize: "20px",
                fontWeight: 600,
                color: tok.text.primary,
                textAlign: "center",
                mt: 0.5,
              }}
            >
              {`${percentageOverallPci.toFixed(2)}%`}
            </Typography>
          </Box>
        }
        onClick={(item) => {
          if (setSelectedEnvFactorID) {
            const envId = rows.find((row) => row.description === item.name)?.env_id;
            if (envId) setSelectedEnvFactorID(envId);
          }
        }}
      />
    </M3Surface>
  );
};

export default CircularPCIChart;
